import React, { useState, useEffect } from 'react';
import ApiClient, { Form } from '../api/client';
import FormList from './FormList';
import FormDialog from './FormDialog';
import FormDetails from './FormDetails';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { AlertIcon, InboxIcon, PlusIcon } from './icons';
import { usePermissions } from '../context/PermissionContext';

interface DashboardProps {
  apiClient: ApiClient;
}

const Dashboard: React.FC<DashboardProps> = ({ apiClient }) => {
  const { hasPermission } = usePermissions();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingForm, setEditingForm] = useState<Form | null>(null);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [deletingFormId, setDeletingFormId] = useState<number | null>(null);

  const canCreate = hasPermission('forms.create');
  const canEdit = hasPermission('forms.edit');
  const canDelete = hasPermission('forms.delete');

  useEffect(() => {
    loadForms();
  }, []);

  // Handle escape key to close modals (only if no other modal is open)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Don't close details modal if FormDialog is open - let it handle its own escape
        if (showDialog) return;
        if (deletingFormId !== null) return;

        if (showDetails) {
          setShowDetails(false);
          setSelectedForm(null);
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showDetails, showDialog, deletingFormId]);

  const loadForms = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.listForms();
      setForms(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async (data: {
    name: string;
    domain: string;
    allowedOrigins: string[];
    description?: string;
    testMode?: boolean;
    spamThreshold?: number | null;
    notificationsEnabled?: boolean;
    spamDetectionDisabled?: boolean;
    adapterType?: 'flat' | 'event_envelope';
    eventTypePath?: string | null;
    unwrapPath?: string | null;
  }) => {
    try {
      if (editingForm) {
        const updated = await apiClient.updateForm(editingForm.id, data);
        setForms(forms.map((f) => (f.id === editingForm.id ? updated : f)));
        setSelectedForm(updated);
        setEditingForm(null);
      } else {
        const newForm = await apiClient.createForm(data);
        setForms([...forms, newForm]);
        setSelectedForm(newForm);
        setShowDetails(true);
      }
      setShowDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save form');
    }
  };

  const handleEditForm = (form: Form) => {
    setEditingForm(form);
    setShowDialog(true);
  };

  const handleDeleteForm = (id: number) => {
    setDeletingFormId(id);
  };

  const handleConfirmDeleteForm = async () => {
    if (deletingFormId === null) return;

    try {
      await apiClient.deleteForm(deletingFormId);
      setForms(forms.filter((f) => f.id !== deletingFormId));
      if (selectedForm?.id === deletingFormId) {
        setSelectedForm(null);
        setShowDetails(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete form');
    } finally {
      setDeletingFormId(null);
    }
  };

  const handleSelectForm = async (form: Form) => {
    try {
      const details = await apiClient.getForm(form.id);
      setSelectedForm(details);
      setShowDetails(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load form details');
    }
  };

  const handleFormUpdated = (updatedForm: Form) => {
    setForms(forms.map((f) => (f.id === updatedForm.id ? updatedForm : f)));
    setSelectedForm(updatedForm);
  };

  return (
    <div className="manager-page forms-page">
      {error && (
        <div className="alert alert-error">
          <AlertIcon size={15} />
          <span>{error}</span>
        </div>
      )}

      <div className="list-page-header">
        <h2>Forms</h2>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => setShowDialog(true)}>
            <PlusIcon />
            Create Form
          </button>
        )}
      </div>

      <div className="forms-table-shell">
        {loading ? (
          <div className="empty-state">
            <div className="loading-spinner"></div>
            <p>Loading forms...</p>
          </div>
        ) : forms.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><InboxIcon size={28} /></div>
            <h3>No forms yet</h3>
            <p>Create your first form to get started</p>
          </div>
        ) : (
          <FormList
            forms={forms}
            onSelectForm={handleSelectForm}
            onDeleteForm={canDelete ? handleDeleteForm : undefined}
            selectedId={selectedForm?.id}
          />
        )}
      </div>

      {/* Form Details Modal */}
      {showDetails && selectedForm && (
        <FormDetails
          form={selectedForm}
          apiClient={apiClient}
          onClose={() => {
            setShowDetails(false);
            setSelectedForm(null);
          }}
          onFormUpdated={handleFormUpdated}
          onEditForm={canEdit ? handleEditForm : undefined}
        />
      )}

      {showDialog && (
        <FormDialog
          apiClient={apiClient}
          form={editingForm || undefined}
          onClose={() => {
            setShowDialog(false);
            setEditingForm(null);
          }}
          onSubmit={handleCreateForm}
        />
      )}

      {deletingFormId !== null && (
        <ConfirmDeleteModal
          title="Delete Form"
          message="Delete this form permanently? This action cannot be undone."
          confirmButtonLabel="Delete"
          onConfirm={handleConfirmDeleteForm}
          onCancel={() => setDeletingFormId(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
